// Learning Analytics and GPA Calculation System
import { database } from '../supabase';

/**
 * Calculate GPA based on quiz attempts and flashcard progress
 * @param {Array} quizAttempts - Array of quiz attempt objects
 * @param {Array} flashcardProgress - Array of flashcard progress objects
 * @param {Object} options - Calculation options
 * @returns {Object} GPA calculation results
 */
export const calculateGPA = (quizAttempts = [], flashcardProgress = [], options = {}) => {
  const {
    quizWeight = 0.7,        // 70% weight for quiz performance
    flashcardWeight = 0.3,   // 30% weight for flashcard mastery
    minAttempts = 1,         // Minimum attempts required for valid GPA
    scale = 4.0              // GPA scale (4.0 scale)
  } = options;

  // Calculate quiz component GPA
  const quizGPA = calculateQuizGPA(quizAttempts, scale);
  
  // Calculate flashcard component GPA
  const flashcardGPA = calculateFlashcardGPA(flashcardProgress, scale);
  
  // Check if we have enough data for valid GPA
  const hasValidData = quizAttempts.length >= minAttempts || flashcardProgress.length > 0;
  
  if (!hasValidData) {
    return {
      overallGPA: 0,
      quizGPA: 0,
      flashcardGPA: 0,
      isValid: false,
      dataPoints: {
        quizAttempts: quizAttempts.length,
        flashcardCards: flashcardProgress.length
      }
    };
  }

  // Calculate weighted overall GPA
  const overallGPA = (quizGPA * quizWeight) + (flashcardGPA * flashcardWeight);
  
  return {
    overallGPA: Math.min(scale, Math.max(0, overallGPA)),
    quizGPA: Math.min(scale, Math.max(0, quizGPA)),
    flashcardGPA: Math.min(scale, Math.max(0, flashcardGPA)),
    isValid: true,
    dataPoints: {
      quizAttempts: quizAttempts.length,
      flashcardCards: flashcardProgress.length
    },
    weights: {
      quiz: quizWeight,
      flashcard: flashcardWeight
    }
  };
};

/**
 * Calculate GPA component from quiz attempts
 * @param {Array} quizAttempts - Array of quiz attempt objects
 * @param {number} scale - GPA scale (default 4.0)
 * @returns {number} Quiz GPA (0-4.0)
 */
export const calculateQuizGPA = (quizAttempts = [], scale = 4.0) => {
  if (!quizAttempts || quizAttempts.length === 0) {
    return 0;
  }

  // Calculate average percentage score
  const totalPercentage = quizAttempts.reduce((sum, attempt) => {
    return sum + (attempt.percentage || 0);
  }, 0);
  
  const averagePercentage = totalPercentage / quizAttempts.length;
  
  // Convert percentage to GPA scale
  const quizGPA = (averagePercentage / 100) * scale;
  
  return Math.min(scale, Math.max(0, quizGPA));
};

/**
 * Calculate GPA component from flashcard progress
 * @param {Array} flashcardProgress - Array of flashcard progress objects
 * @param {number} scale - GPA scale (default 4.0)
 * @returns {number} Flashcard GPA (0-4.0)
 */
export const calculateFlashcardGPA = (flashcardProgress = [], scale = 4.0) => {
  if (!flashcardProgress || flashcardProgress.length === 0) {
    return 0;
  }

  // Calculate average mastery level
  const totalMastery = flashcardProgress.reduce((sum, card) => {
    return sum + (card.mastery_level || 0);
  }, 0);
  
  const averageMastery = totalMastery / flashcardProgress.length;
  
  // Convert mastery percentage to GPA scale
  const flashcardGPA = (averageMastery / 100) * scale;
  
  return Math.min(scale, Math.max(0, flashcardGPA));
};

/**
 * Calculate GPA for a specific document
 * @param {string} userId - User ID
 * @param {string} documentName - Document name
 * @returns {Promise<Object>} Document-specific GPA calculation
 */
export const calculateDocumentGPA = async (userId, documentName) => {
  try {
    // Fetch quiz attempts for this document
    const quizAttempts = await database.getQuizAttempts(userId, documentName);
    
    // Fetch flashcard progress for this document
    // Note: We'll need to filter flashcard progress by document in the future
    const allFlashcardProgress = await database.getFlashcardProgress(userId);
    const documentFlashcardProgress = allFlashcardProgress.filter(progress => 
      progress.document_name === documentName
    );
    
    // Calculate GPA
    const gpaResult = calculateGPA(quizAttempts, documentFlashcardProgress);
    
    return {
      documentName,
      ...gpaResult,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error calculating document GPA:', error);
    return {
      documentName,
      overallGPA: 0,
      quizGPA: 0,
      flashcardGPA: 0,
      isValid: false,
      error: error.message
    };
  }
};

/**
 * Calculate overall GPA across all documents
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Overall GPA calculation
 */
export const calculateOverallGPA = async (userId) => {
  try {
    // Fetch all quiz attempts
    const allQuizAttempts = await database.getQuizAttempts(userId);
    
    // Fetch all flashcard progress
    const allFlashcardProgress = await database.getFlashcardProgress(userId);
    
    // Calculate overall GPA
    const gpaResult = calculateGPA(allQuizAttempts, allFlashcardProgress);
    
    return {
      type: 'overall',
      ...gpaResult,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error calculating overall GPA:', error);
    return {
      type: 'overall',
      overallGPA: 0,
      quizGPA: 0,
      flashcardGPA: 0,
      isValid: false,
      error: error.message
    };
  }
};

/**
 * Get GPA grade letter from numeric GPA
 * @param {number} gpa - Numeric GPA (0-4.0)
 * @returns {string} Grade letter (A+, A, A-, B+, B, B-, C+, C, C-, D+, D, F)
 */
export const getGPAGrade = (gpa) => {
  if (gpa >= 3.7) return 'A+';
  if (gpa >= 3.3) return 'A';
  if (gpa >= 3.0) return 'A-';
  if (gpa >= 2.7) return 'B+';
  if (gpa >= 2.3) return 'B';
  if (gpa >= 2.0) return 'B-';
  if (gpa >= 1.7) return 'C+';
  if (gpa >= 1.3) return 'C';
  if (gpa >= 1.0) return 'C-';
  if (gpa >= 0.7) return 'D+';
  if (gpa >= 0.3) return 'D';
  return 'F';
};

/**
 * Get GPA color for UI display
 * @param {number} gpa - Numeric GPA (0-4.0)
 * @returns {string} CSS color class or hex color
 */
export const getGPAColor = (gpa) => {
  if (gpa >= 3.5) return '#10b981'; // Green
  if (gpa >= 3.0) return '#3b82f6'; // Blue
  if (gpa >= 2.5) return '#f59e0b'; // Yellow
  if (gpa >= 2.0) return '#f97316'; // Orange
  return '#ef4444'; // Red
};

/**
 * Extract topics from question text using keyword analysis
 * @param {string} questionText - The question text to analyze
 * @returns {Array} Array of extracted topics/keywords
 */
export const extractTopicsFromQuestion = (questionText) => {
  if (!questionText) return [];
  
  // Common academic keywords and topics
  const topicKeywords = {
    'biology': ['cell', 'dna', 'protein', 'evolution', 'genetics', 'organism', 'biology', 'biological'],
    'chemistry': ['molecule', 'atom', 'reaction', 'compound', 'element', 'chemistry', 'chemical', 'bond'],
    'physics': ['force', 'energy', 'motion', 'velocity', 'acceleration', 'physics', 'physical', 'mechanics'],
    'mathematics': ['equation', 'function', 'derivative', 'integral', 'algebra', 'geometry', 'calculus', 'math'],
    'history': ['war', 'battle', 'century', 'empire', 'revolution', 'historical', 'history', 'ancient'],
    'literature': ['author', 'novel', 'poem', 'character', 'theme', 'literature', 'literary', 'writing'],
    'psychology': ['behavior', 'cognitive', 'mental', 'brain', 'psychology', 'psychological', 'therapy'],
    'economics': ['market', 'economy', 'supply', 'demand', 'inflation', 'economics', 'economic', 'financial']
  };
  
  const text = questionText.toLowerCase();
  const foundTopics = [];
  
  // Check for topic keywords
  Object.entries(topicKeywords).forEach(([topic, keywords]) => {
    const hasKeyword = keywords.some(keyword => text.includes(keyword));
    if (hasKeyword) {
      foundTopics.push(topic);
    }
  });
  
  // Extract potential chapter/section references
  const chapterMatch = text.match(/chapter\s+(\d+)|section\s+(\d+)|part\s+(\d+)/i);
  if (chapterMatch) {
    const chapterNum = chapterMatch[1] || chapterMatch[2] || chapterMatch[3];
    foundTopics.push(`chapter_${chapterNum}`);
  }
  
  // Extract potential concept keywords (words that appear frequently in academic contexts)
  const conceptWords = text.match(/\b(concept|theory|principle|method|process|system|model|approach)\b/gi);
  if (conceptWords) {
    foundTopics.push('concepts');
  }
  
  return [...new Set(foundTopics)]; // Remove duplicates
};

/**
 * Analyze weak topics from quiz attempts
 * @param {Array} quizAttempts - Array of quiz attempt objects
 * @returns {Object} Analysis of weak and strong topics
 */
export const analyzeWeakTopics = (quizAttempts = []) => {
  if (!quizAttempts || quizAttempts.length === 0) {
    return {
      weakTopics: [],
      strongTopics: [],
      topicStats: {},
      overallAccuracy: 0
    };
  }
  
  const topicStats = {};
  let totalQuestions = 0;
  let correctAnswers = 0;
  
  // Analyze each quiz attempt
  quizAttempts.forEach(attempt => {
    if (!attempt.question_answers || !Array.isArray(attempt.question_answers)) {
      return;
    }
    
    attempt.question_answers.forEach(qa => {
      totalQuestions++;
      if (qa.is_correct) correctAnswers++;
      
      // Extract topics from question
      const topics = extractTopicsFromQuestion(qa.question_text);
      
      topics.forEach(topic => {
        if (!topicStats[topic]) {
          topicStats[topic] = {
            total: 0,
            correct: 0,
            accuracy: 0,
            questions: []
          };
        }
        
        topicStats[topic].total++;
        topicStats[topic].questions.push({
          question: qa.question_text,
          isCorrect: qa.is_correct,
          selectedAnswer: qa.selected_answer,
          correctAnswer: qa.correct_answer
        });
        
        if (qa.is_correct) {
          topicStats[topic].correct++;
        }
      });
    });
  });
  
  // Calculate accuracy for each topic
  Object.keys(topicStats).forEach(topic => {
    const stats = topicStats[topic];
    stats.accuracy = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
  });
  
  // Categorize topics as weak or strong
  const weakTopics = [];
  const strongTopics = [];
  
  Object.entries(topicStats).forEach(([topic, stats]) => {
    if (stats.total >= 2) { // Only include topics with at least 2 questions
      if (stats.accuracy < 60) {
        weakTopics.push({
          topic,
          accuracy: stats.accuracy,
          totalQuestions: stats.total,
          correctAnswers: stats.correct,
          weaknessScore: 100 - stats.accuracy
        });
      } else if (stats.accuracy >= 80) {
        strongTopics.push({
          topic,
          accuracy: stats.accuracy,
          totalQuestions: stats.total,
          correctAnswers: stats.correct,
          strengthScore: stats.accuracy
        });
      }
    }
  });
  
  // Sort by weakness/strength score
  weakTopics.sort((a, b) => b.weaknessScore - a.weaknessScore);
  strongTopics.sort((a, b) => b.strengthScore - a.strengthScore);
  
  const overallAccuracy = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
  
  return {
    weakTopics,
    strongTopics,
    topicStats,
    overallAccuracy,
    totalQuestions,
    correctAnswers
  };
};

/**
 * Analyze flashcard mastery by topic
 * @param {Array} flashcardProgress - Array of flashcard progress objects
 * @param {Array} flashcardData - Array of flashcard data objects
 * @returns {Object} Analysis of flashcard mastery by topic
 */
export const analyzeFlashcardMastery = (flashcardProgress = [], flashcardData = []) => {
  if (!flashcardProgress || flashcardProgress.length === 0) {
    return {
      masteredTopics: [],
      strugglingTopics: [],
      masteryStats: {},
      overallMastery: 0
    };
  }
  
  const masteryStats = {};
  let totalCards = 0;
  let totalMastery = 0;
  
  // Analyze each flashcard's progress
  flashcardProgress.forEach(progress => {
    totalCards++;
    totalMastery += progress.mastery_level || 0;
    
    // Find the corresponding flashcard data
    const flashcard = flashcardData.find(fc => fc.id === progress.flashcard_id);
    if (!flashcard || !flashcard.flashcard_data) return;
    
    const cardData = flashcard.flashcard_data[progress.card_index];
    if (!cardData) return;
    
    // Extract topics from flashcard content
    const frontTopics = extractTopicsFromQuestion(cardData.front || '');
    const backTopics = extractTopicsFromQuestion(cardData.back || '');
    const allTopics = [...new Set([...frontTopics, ...backTopics])];
    
    allTopics.forEach(topic => {
      if (!masteryStats[topic]) {
        masteryStats[topic] = {
          totalCards: 0,
          totalMastery: 0,
          averageMastery: 0,
          masteredCards: 0,
          strugglingCards: 0
        };
      }
      
      masteryStats[topic].totalCards++;
      masteryStats[topic].totalMastery += progress.mastery_level || 0;
      
      if (progress.mastery_level >= 80) {
        masteryStats[topic].masteredCards++;
      } else if (progress.mastery_level < 50) {
        masteryStats[topic].strugglingCards++;
      }
    });
  });
  
  // Calculate average mastery for each topic
  Object.keys(masteryStats).forEach(topic => {
    const stats = masteryStats[topic];
    stats.averageMastery = stats.totalCards > 0 ? stats.totalMastery / stats.totalCards : 0;
  });
  
  // Categorize topics
  const masteredTopics = [];
  const strugglingTopics = [];
  
  Object.entries(masteryStats).forEach(([topic, stats]) => {
    if (stats.totalCards >= 2) { // Only include topics with at least 2 cards
      if (stats.averageMastery >= 80) {
        masteredTopics.push({
          topic,
          averageMastery: stats.averageMastery,
          totalCards: stats.totalCards,
          masteredCards: stats.masteredCards
        });
      } else if (stats.averageMastery < 50) {
        strugglingTopics.push({
          topic,
          averageMastery: stats.averageMastery,
          totalCards: stats.totalCards,
          strugglingCards: stats.strugglingCards
        });
      }
    }
  });
  
  // Sort by mastery level
  masteredTopics.sort((a, b) => b.averageMastery - a.averageMastery);
  strugglingTopics.sort((a, b) => a.averageMastery - b.averageMastery);
  
  const overallMastery = totalCards > 0 ? totalMastery / totalCards : 0;
  
  return {
    masteredTopics,
    strugglingTopics,
    masteryStats,
    overallMastery,
    totalCards
  };
};

/**
 * Generate detailed study recommendations based on analysis
 * @param {Object} topicAnalysis - Result from analyzeWeakTopics
 * @param {Object} flashcardAnalysis - Result from analyzeFlashcardMastery
 * @returns {Array} Array of detailed study recommendations
 */
export const generateStudyRecommendations = (topicAnalysis, flashcardAnalysis) => {
  const recommendations = [];
  
  // Quiz-based recommendations with detailed analysis
  if (topicAnalysis.weakTopics && topicAnalysis.weakTopics.length > 0) {
    topicAnalysis.weakTopics.slice(0, 3).forEach((weakTopic, index) => {
      const priority = index === 0 ? 'high' : index === 1 ? 'medium' : 'low';
      
      // Get specific question examples for this topic
      const topicQuestions = topicAnalysis.topicStats[weakTopic.topic]?.questions || [];
      const wrongQuestions = topicQuestions.filter(q => !q.isCorrect);
      const commonMistakes = wrongQuestions.slice(0, 2);
      
      let detailedDescription = `Your performance in ${weakTopic.topic} needs immediate attention. You're scoring only ${weakTopic.accuracy.toFixed(1)}% (${weakTopic.correctAnswers}/${weakTopic.totalQuestions} correct) on ${weakTopic.topic} questions. `;
      
      if (commonMistakes.length > 0) {
        detailedDescription += `Based on your recent attempts, you're consistently struggling with questions like "${commonMistakes[0].question.substring(0, 80)}..." `;
        if (commonMistakes.length > 1) {
          detailedDescription += `and "${commonMistakes[1].question.substring(0, 60)}...". `;
        }
      }
      
      detailedDescription += `This suggests you need to revisit the fundamental concepts in ${weakTopic.topic}. I recommend focusing on the core principles and definitions before attempting more advanced questions. `;
      
      if (weakTopic.accuracy < 40) {
        detailedDescription += `With such a low accuracy rate, consider starting with basic review materials and gradually building up to more complex problems.`;
      } else if (weakTopic.accuracy < 60) {
        detailedDescription += `You're close to passing but need to strengthen your understanding of key concepts. Focus on practice problems and review explanations carefully.`;
      }
      
      recommendations.push({
        type: 'quiz_retake',
        priority: priority,
        title: `Critical: Improve ${weakTopic.topic} Performance`,
        description: detailedDescription,
        action: `Retake ${weakTopic.topic} quizzes after reviewing core concepts`,
        topic: weakTopic.topic,
        accuracy: weakTopic.accuracy,
        totalQuestions: weakTopic.totalQuestions,
        correctAnswers: weakTopic.correctAnswers,
        weaknessScore: weakTopic.weaknessScore
      });
    });
  }
  
  // Flashcard-based recommendations with detailed analysis
  if (flashcardAnalysis.strugglingTopics && flashcardAnalysis.strugglingTopics.length > 0) {
    flashcardAnalysis.strugglingTopics.slice(0, 2).forEach((strugglingTopic, index) => {
      const priority = index === 0 ? 'high' : 'medium';
      
      let detailedDescription = `Your flashcard mastery in ${strugglingTopic.topic} is concerning at only ${strugglingTopic.averageMastery.toFixed(1)}%. `;
      detailedDescription += `Out of ${strugglingTopic.totalCards} flashcards, you're struggling with ${strugglingTopic.strugglingCards} cards, which means you haven't memorized the key concepts yet. `;
      
      if (strugglingTopic.averageMastery < 30) {
        detailedDescription += `This extremely low mastery level indicates you need to start from the basics. Focus on understanding the fundamental concepts before trying to memorize them. `;
        detailedDescription += `Consider breaking down complex topics into smaller, more manageable pieces and study them systematically.`;
      } else if (strugglingTopic.averageMastery < 50) {
        detailedDescription += `You're making some progress but still need significant improvement. The concepts aren't sticking, which suggests you might need to change your study approach. `;
        detailedDescription += `Try using different memorization techniques like spaced repetition, creating mental associations, or explaining concepts out loud.`;
      }
      
      detailedDescription += ` I recommend studying these flashcards daily for at least 15-20 minutes until you can consistently recall the information without hesitation.`;
      
      recommendations.push({
        type: 'flashcard_review',
        priority: priority,
        title: `Urgent: Master ${strugglingTopic.topic} Flashcards`,
        description: detailedDescription,
        action: `Study ${strugglingTopic.topic} flashcards daily with spaced repetition`,
        topic: strugglingTopic.topic,
        averageMastery: strugglingTopic.averageMastery,
        totalCards: strugglingTopic.totalCards,
        strugglingCards: strugglingTopic.strugglingCards
      });
    });
  }
  
  // General improvement recommendations with specific guidance
  if (topicAnalysis.overallAccuracy < 70) {
    let detailedDescription = `Your overall quiz performance of ${topicAnalysis.overallAccuracy.toFixed(1)}% indicates significant gaps in your understanding. `;
    detailedDescription += `You've answered ${topicAnalysis.correctAnswers} out of ${topicAnalysis.totalQuestions} questions correctly across all topics. `;
    
    if (topicAnalysis.overallAccuracy < 50) {
      detailedDescription += `This performance level suggests you need to fundamentally change your study approach. `;
      detailedDescription += `Instead of rushing through quizzes, focus on thoroughly understanding the material first. `;
      detailedDescription += `Consider reviewing your study materials more carefully, taking notes, and ensuring you understand each concept before moving on. `;
      detailedDescription += `You might benefit from breaking down complex topics into smaller sections and mastering each one before attempting quizzes.`;
    } else if (topicAnalysis.overallAccuracy < 70) {
      detailedDescription += `You're showing some understanding but need to improve your retention and application of knowledge. `;
      detailedDescription += `The issue might be that you're not spending enough time reviewing incorrect answers and understanding why you got them wrong. `;
      detailedDescription += `Make sure to read explanations carefully and connect new information to what you already know. `;
      detailedDescription += `Consider using active recall techniques and testing yourself regularly rather than just reading through materials.`;
    }
    
    recommendations.push({
      type: 'general_study',
      priority: 'high',
      title: 'Overall Study Strategy Needs Improvement',
      description: detailedDescription,
      action: 'Revise study approach and focus on understanding before testing',
      topic: 'general',
      overallAccuracy: topicAnalysis.overallAccuracy,
      totalQuestions: topicAnalysis.totalQuestions,
      correctAnswers: topicAnalysis.correctAnswers
    });
  }
  
  // Positive reinforcement with specific achievements
  if (topicAnalysis.strongTopics && topicAnalysis.strongTopics.length > 0) {
    topicAnalysis.strongTopics.slice(0, 2).forEach((strongTopic, index) => {
      const priority = 'low';
      
      let detailedDescription = `Excellent work in ${strongTopic.topic}! You're demonstrating mastery with ${strongTopic.accuracy.toFixed(1)}% accuracy `;
      detailedDescription += `(${strongTopic.correctAnswers}/${strongTopic.totalQuestions} correct). `;
      
      if (strongTopic.accuracy >= 95) {
        detailedDescription += `This near-perfect performance shows you have a deep understanding of ${strongTopic.topic} concepts. `;
        detailedDescription += `You're consistently applying your knowledge correctly and can handle both basic and complex questions. `;
        detailedDescription += `Consider using this strength to help with other subjects by finding connections between topics.`;
      } else if (strongTopic.accuracy >= 85) {
        detailedDescription += `Your strong performance indicates solid comprehension of ${strongTopic.topic}. `;
        detailedDescription += `You're making very few mistakes and showing good retention of the material. `;
        detailedDescription += `Keep up this level of understanding and consider helping others or teaching the concepts to reinforce your learning.`;
      }
      
      recommendations.push({
        type: 'positive_feedback',
        priority: priority,
        title: `Outstanding: ${strongTopic.topic} Mastery`,
        description: detailedDescription,
        action: 'Continue excelling and consider advanced topics in this area',
        topic: strongTopic.topic,
        accuracy: strongTopic.accuracy,
        totalQuestions: strongTopic.totalQuestions,
        correctAnswers: strongTopic.correctAnswers,
        strengthScore: strongTopic.strengthScore
      });
    });
  }
  
  // Mixed performance analysis
  if (topicAnalysis.weakTopics.length > 0 && topicAnalysis.strongTopics.length > 0) {
    const topWeak = topicAnalysis.weakTopics[0];
    const topStrong = topicAnalysis.strongTopics[0];
    
    recommendations.push({
      type: 'mixed_performance',
      priority: 'medium',
      title: 'Study Strategy: Leverage Your Strengths',
      description: `You're showing a clear pattern in your performance. While you excel in ${topStrong.topic} (${topStrong.accuracy.toFixed(1)}% accuracy), you're struggling significantly with ${topWeak.topic} (${topWeak.accuracy.toFixed(1)}% accuracy). This suggests you might be using different study approaches for different subjects. Consider applying the same successful strategies you use for ${topStrong.topic} to improve your ${topWeak.topic} performance. Analyze what makes ${topStrong.topic} easier for you - is it the way you study, the type of practice, or your interest level? Try to replicate those successful methods with your weaker subjects.`,
      action: `Apply successful ${topStrong.topic} study methods to ${topWeak.topic}`,
      topic: 'mixed',
      strongTopic: topStrong.topic,
      weakTopic: topWeak.topic
    });
  }
  
  return recommendations;
};

/**
 * Update learning analytics for a specific document
 * @param {string} userId - User ID
 * @param {string} documentName - Document name
 * @param {Object} options - Update options
 * @returns {Promise<Object>} Updated analytics data
 */
export const updateDocumentAnalytics = async (userId, documentName, options = {}) => {
  try {
    console.log(`🔄 Updating analytics for document: ${documentName}`);
    
    // Fetch current data
    const quizAttempts = await database.getQuizAttempts(userId, documentName);
    const allFlashcardProgress = await database.getFlashcardProgress(userId);
    const documentFlashcardProgress = allFlashcardProgress.filter(progress => 
      progress.document_name === documentName
    );
    
    // Calculate GPA
    const gpaResult = calculateGPA(quizAttempts, documentFlashcardProgress);
    
    // Analyze topics
    const topicAnalysis = analyzeWeakTopics(quizAttempts);
    
    // Analyze flashcard mastery
    const flashcardData = await database.getUserFlashcards(userId);
    const flashcardAnalysis = analyzeFlashcardMastery(documentFlashcardProgress, flashcardData);
    
    // Generate recommendations
    const recommendations = generateStudyRecommendations(topicAnalysis, flashcardAnalysis);
    
    // Calculate study time (if available)
    const studyTimeMinutes = calculateStudyTime(quizAttempts, documentFlashcardProgress);
    
    // Prepare analytics data
    const analyticsData = {
      subjectArea: extractSubjectFromDocument(documentName),
      gpaScore: gpaResult.overallGPA,
      totalQuizAttempts: quizAttempts.length,
      averageQuizScore: topicAnalysis.overallAccuracy,
      totalFlashcardStudied: documentFlashcardProgress.length,
      flashcardMasteryPercentage: flashcardAnalysis.overallMastery,
      weakTopics: topicAnalysis.weakTopics,
      strongTopics: topicAnalysis.strongTopics,
      studyTimeMinutes: studyTimeMinutes,
      lastUpdated: new Date().toISOString()
    };
    
    // Update in database
    const updatedAnalytics = await database.updateLearningAnalytics(userId, documentName, analyticsData);
    
    console.log(`✅ Analytics updated for ${documentName}:`, {
      gpa: gpaResult.overallGPA,
      quizAttempts: quizAttempts.length,
      flashcardProgress: documentFlashcardProgress.length,
      weakTopics: topicAnalysis.weakTopics.length,
      strongTopics: topicAnalysis.strongTopics.length
    });
    
    return {
      analytics: updatedAnalytics,
      gpa: gpaResult,
      topicAnalysis,
      flashcardAnalysis,
      recommendations
    };
    
  } catch (error) {
    console.error('❌ Error updating document analytics:', error);
    throw error;
  }
};

/**
 * Update overall learning analytics across all documents
 * @param {string} userId - User ID
 * @param {Object} options - Update options
 * @returns {Promise<Object>} Updated overall analytics
 */
export const updateOverallAnalytics = async (userId, options = {}) => {
  try {
    console.log(`🔄 Updating overall analytics for user: ${userId}`);
    
    // Fetch all data
    const allQuizAttempts = await database.getQuizAttempts(userId);
    const allFlashcardProgress = await database.getFlashcardProgress(userId);
    
    // Calculate overall GPA
    const gpaResult = calculateGPA(allQuizAttempts, allFlashcardProgress);
    
    // Analyze all topics
    const topicAnalysis = analyzeWeakTopics(allQuizAttempts);
    
    // Analyze all flashcard mastery
    const flashcardData = await database.getUserFlashcards(userId);
    const flashcardAnalysis = analyzeFlashcardMastery(allFlashcardProgress, flashcardData);
    
    // Generate overall recommendations
    const recommendations = generateStudyRecommendations(topicAnalysis, flashcardAnalysis);
    
    // Calculate total study time
    const studyTimeMinutes = calculateStudyTime(allQuizAttempts, allFlashcardProgress);
    
    // Get unique documents
    const documents = [...new Set([
      ...allQuizAttempts.map(attempt => attempt.document_name),
      ...allFlashcardProgress.map(progress => progress.document_name)
    ].filter(Boolean))];
    
    // Prepare overall analytics data
    const analyticsData = {
      subjectArea: 'Multiple Subjects',
      gpaScore: gpaResult.overallGPA,
      totalQuizAttempts: allQuizAttempts.length,
      averageQuizScore: topicAnalysis.overallAccuracy,
      totalFlashcardStudied: allFlashcardProgress.length,
      flashcardMasteryPercentage: flashcardAnalysis.overallMastery,
      weakTopics: topicAnalysis.weakTopics,
      strongTopics: topicAnalysis.strongTopics,
      studyTimeMinutes: studyTimeMinutes,
      totalDocuments: documents.length,
      lastUpdated: new Date().toISOString()
    };
    
    // Update in database (using a special key for overall analytics)
    const updatedAnalytics = await database.updateLearningAnalytics(userId, 'OVERALL', analyticsData);
    
    console.log(`✅ Overall analytics updated:`, {
      gpa: gpaResult.overallGPA,
      totalQuizAttempts: allQuizAttempts.length,
      totalFlashcardProgress: allFlashcardProgress.length,
      documents: documents.length,
      weakTopics: topicAnalysis.weakTopics.length,
      strongTopics: topicAnalysis.strongTopics.length
    });
    
    return {
      analytics: updatedAnalytics,
      gpa: gpaResult,
      topicAnalysis,
      flashcardAnalysis,
      recommendations,
      documents
    };
    
  } catch (error) {
    console.error('❌ Error updating overall analytics:', error);
    throw error;
  }
};

/**
 * Trigger analytics update after quiz completion
 * @param {string} userId - User ID
 * @param {string} documentName - Document name
 * @param {Object} quizAttempt - Quiz attempt data
 * @returns {Promise<void>}
 */
export const triggerQuizAnalyticsUpdate = async (userId, documentName, quizAttempt) => {
  try {
    console.log(`🎯 Triggering quiz analytics update for ${documentName}`);
    
    // Real-time update for immediate feedback
    const analytics = await updateDocumentAnalytics(userId, documentName, {
      trigger: 'quiz_completion',
      quizAttempt: quizAttempt
    });
    
    // Also update overall analytics
    await updateOverallAnalytics(userId, {
      trigger: 'quiz_completion'
    });
    
    console.log(`✅ Quiz analytics update completed for ${documentName}`);
    return analytics;
    
  } catch (error) {
    console.error('❌ Error in quiz analytics update:', error);
    // Don't throw - analytics update shouldn't break quiz flow
  }
};

/**
 * Trigger analytics update after flashcard study session
 * @param {string} userId - User ID
 * @param {string} documentName - Document name
 * @param {Array} progressUpdates - Array of progress updates
 * @returns {Promise<void>}
 */
export const triggerFlashcardAnalyticsUpdate = async (userId, documentName, progressUpdates) => {
  try {
    console.log(`🎯 Triggering flashcard analytics update for ${documentName}`);
    
    // Batch update for flashcard sessions (less frequent)
    const analytics = await updateDocumentAnalytics(userId, documentName, {
      trigger: 'flashcard_study',
      progressUpdates: progressUpdates
    });
    
    // Also update overall analytics
    await updateOverallAnalytics(userId, {
      trigger: 'flashcard_study'
    });
    
    console.log(`✅ Flashcard analytics update completed for ${documentName}`);
    return analytics;
    
  } catch (error) {
    console.error('❌ Error in flashcard analytics update:', error);
    // Don't throw - analytics update shouldn't break flashcard flow
  }
};

/**
 * Calculate total study time from attempts and progress
 * @param {Array} quizAttempts - Quiz attempts
 * @param {Array} flashcardProgress - Flashcard progress
 * @returns {number} Total study time in minutes
 */
const calculateStudyTime = (quizAttempts = [], flashcardProgress = []) => {
  let totalMinutes = 0;
  
  // Add quiz time
  quizAttempts.forEach(attempt => {
    if (attempt.time_spent_seconds) {
      totalMinutes += attempt.time_spent_seconds / 60;
    }
  });
  
  // Estimate flashcard study time (5 minutes per card studied)
  const studiedCards = flashcardProgress.filter(progress => 
    progress.status !== 'not_studied'
  ).length;
  totalMinutes += studiedCards * 5;
  
  return Math.round(totalMinutes);
};

/**
 * Extract subject area from document name
 * @param {string} documentName - Document name
 * @returns {string} Extracted subject area
 */
const extractSubjectFromDocument = (documentName) => {
  if (!documentName) return 'General';
  
  const name = documentName.toLowerCase();
  
  if (name.includes('biology') || name.includes('bio')) return 'Biology';
  if (name.includes('chemistry') || name.includes('chem')) return 'Chemistry';
  if (name.includes('physics')) return 'Physics';
  if (name.includes('math') || name.includes('calculus') || name.includes('algebra')) return 'Mathematics';
  if (name.includes('history')) return 'History';
  if (name.includes('literature') || name.includes('english')) return 'Literature';
  if (name.includes('psychology') || name.includes('psych')) return 'Psychology';
  if (name.includes('economics') || name.includes('econ')) return 'Economics';
  if (name.includes('computer') || name.includes('programming') || name.includes('cs')) return 'Computer Science';
  
  return 'General';
};

/**
 * Batch update analytics for multiple documents
 * @param {string} userId - User ID
 * @param {Array} documentNames - Array of document names
 * @returns {Promise<Object>} Batch update results
 */
export const batchUpdateAnalytics = async (userId, documentNames = []) => {
  try {
    console.log(`🔄 Batch updating analytics for ${documentNames.length} documents`);
    
    const results = {};
    
    // Update each document
    for (const documentName of documentNames) {
      try {
        results[documentName] = await updateDocumentAnalytics(userId, documentName);
      } catch (error) {
        console.error(`❌ Error updating analytics for ${documentName}:`, error);
        results[documentName] = { error: error.message };
      }
    }
    
    // Update overall analytics
    results.OVERALL = await updateOverallAnalytics(userId);
    
    console.log(`✅ Batch analytics update completed for ${documentNames.length} documents`);
    return results;
    
  } catch (error) {
    console.error('❌ Error in batch analytics update:', error);
    throw error;
  }
};

// ============================================================================
// ANALYTICS HELPER FUNCTIONS
// ============================================================================

/**
 * Get comprehensive analytics data for a user
 * @param {string} userId - User ID
 * @param {string} documentName - Optional document name filter
 * @returns {Promise<Object>} Complete analytics data
 */
export const getAnalyticsData = async (userId, documentName = null) => {
  try {
    console.log(`📊 Fetching analytics data for user: ${userId}${documentName ? `, document: ${documentName}` : ''}`);
    
    // Get analytics from database
    const analytics = await database.getLearningAnalytics(userId, documentName);
    
    // Get raw data for calculations
    const quizAttempts = await database.getQuizAttempts(userId, documentName);
    const flashcardProgress = await database.getFlashcardProgress(userId);
    const flashcardData = await database.getUserFlashcards(userId);
    
    // Filter flashcard progress by document if specified
    const relevantFlashcardProgress = documentName 
      ? flashcardProgress.filter(progress => progress.document_name === documentName)
      : flashcardProgress;
    
    // Calculate fresh analytics
    const gpaResult = calculateGPA(quizAttempts, relevantFlashcardProgress);
    const topicAnalysis = analyzeWeakTopics(quizAttempts);
    const flashcardAnalysis = analyzeFlashcardMastery(relevantFlashcardProgress, flashcardData);
    const recommendations = generateStudyRecommendations(topicAnalysis, flashcardAnalysis);
    
    // Get study time
    const studyTimeMinutes = calculateStudyTime(quizAttempts, relevantFlashcardProgress);
    
    // Get documents list
    const documents = [...new Set([
      ...quizAttempts.map(attempt => attempt.document_name),
      ...flashcardProgress.map(progress => progress.document_name)
    ].filter(Boolean))];
    
    return {
      analytics,
      gpa: gpaResult,
      topicAnalysis,
      flashcardAnalysis,
      recommendations,
      studyTime: {
        totalMinutes: studyTimeMinutes,
        totalHours: Math.round(studyTimeMinutes / 60 * 10) / 10,
        formatted: formatStudyTime(studyTimeMinutes)
      },
      documents,
      summary: generateAnalyticsSummary(gpaResult, topicAnalysis, flashcardAnalysis, studyTimeMinutes)
    };
    
  } catch (error) {
    console.error('❌ Error fetching analytics data:', error);
    throw error;
  }
};

/**
 * Get analytics summary for dashboard display
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Dashboard-ready analytics summary
 */
export const getAnalyticsSummary = async (userId) => {
  try {
    const analyticsData = await getAnalyticsData(userId);
    
    return {
      gpa: analyticsData.gpa.overallGPA,
      gpaGrade: getGPAGrade(analyticsData.gpa.overallGPA),
      gpaColor: getGPAColor(analyticsData.gpa.overallGPA),
      totalQuizAttempts: analyticsData.topicAnalysis.totalQuestions,
      averageQuizScore: analyticsData.topicAnalysis.overallAccuracy,
      totalFlashcards: analyticsData.flashcardAnalysis.totalCards,
      flashcardMastery: analyticsData.flashcardAnalysis.overallMastery,
      studyTime: analyticsData.studyTime,
      weakTopicsCount: analyticsData.topicAnalysis.weakTopics.length,
      strongTopicsCount: analyticsData.topicAnalysis.strongTopics.length,
      topWeakTopic: analyticsData.topicAnalysis.weakTopics[0]?.topic || null,
      topStrongTopic: analyticsData.topicAnalysis.strongTopics[0]?.topic || null,
      documentsCount: analyticsData.documents.length,
      lastUpdated: analyticsData.analytics?.last_updated || new Date().toISOString()
    };
    
  } catch (error) {
    console.error('❌ Error fetching analytics summary:', error);
    throw error;
  }
};

/**
 * Get document-specific analytics
 * @param {string} userId - User ID
 * @param {string} documentName - Document name
 * @returns {Promise<Object>} Document analytics
 */
export const getDocumentAnalytics = async (userId, documentName) => {
  try {
    const analyticsData = await getAnalyticsData(userId, documentName);
    
    return {
      documentName,
      gpa: analyticsData.gpa.overallGPA,
      gpaGrade: getGPAGrade(analyticsData.gpa.overallGPA),
      gpaColor: getGPAColor(analyticsData.gpa.overallGPA),
      quizAttempts: analyticsData.topicAnalysis.totalQuestions,
      averageQuizScore: analyticsData.topicAnalysis.overallAccuracy,
      flashcardProgress: analyticsData.flashcardAnalysis.totalCards,
      flashcardMastery: analyticsData.flashcardAnalysis.overallMastery,
      studyTime: analyticsData.studyTime,
      weakTopics: analyticsData.topicAnalysis.weakTopics,
      strongTopics: analyticsData.topicAnalysis.strongTopics,
      recommendations: analyticsData.recommendations,
      subjectArea: extractSubjectFromDocument(documentName)
    };
    
  } catch (error) {
    console.error('❌ Error fetching document analytics:', error);
    throw error;
  }
};

/**
 * Get performance trends over time
 * @param {string} userId - User ID
 * @param {string} documentName - Optional document name
 * @param {number} days - Number of days to look back
 * @returns {Promise<Object>} Performance trends
 */
export const getPerformanceTrends = async (userId, documentName = null, days = 30) => {
  try {
    const quizAttempts = await database.getQuizAttempts(userId, documentName);
    const flashcardProgress = await database.getFlashcardProgress(userId);
    
    // Filter by date range
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const recentQuizAttempts = quizAttempts.filter(attempt => 
      new Date(attempt.completed_at) >= cutoffDate
    );
    
    const recentFlashcardProgress = flashcardProgress.filter(progress => 
      new Date(progress.last_studied_at || progress.created_at) >= cutoffDate
    );
    
    // Group by day
    const dailyData = {};
    
    // Process quiz attempts
    recentQuizAttempts.forEach(attempt => {
      const date = new Date(attempt.completed_at).toISOString().split('T')[0];
      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          quizAttempts: 0,
          quizScores: [],
          flashcardProgress: 0,
          studyTime: 0
        };
      }
      dailyData[date].quizAttempts++;
      dailyData[date].quizScores.push(attempt.percentage);
      dailyData[date].studyTime += (attempt.time_spent_seconds || 0) / 60;
    });
    
    // Process flashcard progress
    recentFlashcardProgress.forEach(progress => {
      const date = new Date(progress.last_studied_at || progress.created_at).toISOString().split('T')[0];
      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          quizAttempts: 0,
          quizScores: [],
          flashcardProgress: 0,
          studyTime: 0
        };
      }
      dailyData[date].flashcardProgress++;
      dailyData[date].studyTime += 5; // Estimate 5 minutes per card
    });
    
    // Convert to array and calculate averages
    const trends = Object.values(dailyData).map(day => ({
      ...day,
      averageQuizScore: day.quizScores.length > 0 
        ? day.quizScores.reduce((sum, score) => sum + score, 0) / day.quizScores.length 
        : 0,
      totalActivity: day.quizAttempts + day.flashcardProgress
    })).sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return {
      trends,
      summary: {
        totalDays: trends.length,
        averageDailyQuizAttempts: trends.reduce((sum, day) => sum + day.quizAttempts, 0) / trends.length,
        averageDailyQuizScore: trends.reduce((sum, day) => sum + day.averageQuizScore, 0) / trends.length,
        averageDailyStudyTime: trends.reduce((sum, day) => sum + day.studyTime, 0) / trends.length,
        totalStudyTime: trends.reduce((sum, day) => sum + day.studyTime, 0)
      }
    };
    
  } catch (error) {
    console.error('❌ Error fetching performance trends:', error);
    throw error;
  }
};

/**
 * Format study time for display
 * @param {number} minutes - Study time in minutes
 * @returns {string} Formatted time string
 */
export const formatStudyTime = (minutes) => {
  if (minutes < 60) {
    return `${minutes} min`;
  } else if (minutes < 1440) { // Less than 24 hours
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  } else {
    const days = Math.floor(minutes / 1440);
    const remainingHours = Math.floor((minutes % 1440) / 60);
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
};

/**
 * Format GPA for display
 * @param {number} gpa - GPA value
 * @returns {string} Formatted GPA string
 */
export const formatGPA = (gpa) => {
  return gpa.toFixed(2);
};

/**
 * Format percentage for display
 * @param {number} percentage - Percentage value
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (percentage) => {
  return `${percentage.toFixed(1)}%`;
};

/**
 * Generate analytics summary text
 * @param {Object} gpaResult - GPA calculation result
 * @param {Object} topicAnalysis - Topic analysis result
 * @param {Object} flashcardAnalysis - Flashcard analysis result
 * @param {number} studyTimeMinutes - Study time in minutes
 * @returns {Object} Summary object
 */
const generateAnalyticsSummary = (gpaResult, topicAnalysis, flashcardAnalysis, studyTimeMinutes) => {
  const gpa = gpaResult.overallGPA;
  const grade = getGPAGrade(gpa);
  const color = getGPAColor(gpa);
  
  let performanceLevel = 'Excellent';
  if (gpa < 3.0) performanceLevel = 'Needs Improvement';
  else if (gpa < 3.5) performanceLevel = 'Good';
  else if (gpa < 3.7) performanceLevel = 'Very Good';
  
  const weakTopicsCount = topicAnalysis.weakTopics.length;
  const strongTopicsCount = topicAnalysis.strongTopics.length;
  
  let focusArea = 'Well-rounded performance';
  if (weakTopicsCount > strongTopicsCount) {
    focusArea = `Focus on ${topicAnalysis.weakTopics[0]?.topic || 'weak areas'}`;
  } else if (strongTopicsCount > 0) {
    focusArea = `Strong in ${topicAnalysis.strongTopics[0]?.topic || 'multiple areas'}`;
  }
  
  return {
    performanceLevel,
    gpa,
    grade,
    color,
    focusArea,
    studyTime: formatStudyTime(studyTimeMinutes),
    totalQuizAttempts: topicAnalysis.totalQuestions,
    averageQuizScore: topicAnalysis.overallAccuracy,
    flashcardMastery: flashcardAnalysis.overallMastery,
    weakTopicsCount,
    strongTopicsCount
  };
};

/**
 * Get study recommendations by priority
 * @param {Array} recommendations - Array of recommendations
 * @param {string} priority - Priority level ('high', 'medium', 'low')
 * @returns {Array} Filtered recommendations
 */
export const getRecommendationsByPriority = (recommendations, priority) => {
  return recommendations.filter(rec => rec.priority === priority);
};

/**
 * Get top recommendations (up to limit)
 * @param {Array} recommendations - Array of recommendations
 * @param {number} limit - Maximum number of recommendations
 * @returns {Array} Top recommendations
 */
export const getTopRecommendations = (recommendations, limit = 3) => {
  const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
  return recommendations
    .sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority])
    .slice(0, limit);
};

/**
 * Check if user has sufficient data for analytics
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Data sufficiency check
 */
export const checkDataSufficiency = async (userId) => {
  try {
    const quizAttempts = await database.getQuizAttempts(userId);
    const flashcardProgress = await database.getFlashcardProgress(userId);
    
    const hasQuizData = quizAttempts.length > 0;
    const hasFlashcardData = flashcardProgress.length > 0;
    const hasEnoughQuizData = quizAttempts.length >= 3;
    const hasEnoughFlashcardData = flashcardProgress.length >= 5;
    
    return {
      hasData: hasQuizData || hasFlashcardData,
      hasQuizData,
      hasFlashcardData,
      hasEnoughQuizData,
      hasEnoughFlashcardData,
      isReadyForAnalytics: hasEnoughQuizData || hasEnoughFlashcardData,
      recommendations: generateDataSufficiencyRecommendations(
        hasQuizData, hasFlashcardData, hasEnoughQuizData, hasEnoughFlashcardData
      )
    };
    
  } catch (error) {
    console.error('❌ Error checking data sufficiency:', error);
    throw error;
  }
};

/**
 * Generate recommendations for data sufficiency
 * @param {boolean} hasQuizData - Has any quiz data
 * @param {boolean} hasFlashcardData - Has any flashcard data
 * @param {boolean} hasEnoughQuizData - Has sufficient quiz data
 * @param {boolean} hasEnoughFlashcardData - Has sufficient flashcard data
 * @returns {Array} Data sufficiency recommendations
 */
const generateDataSufficiencyRecommendations = (hasQuizData, hasFlashcardData, hasEnoughQuizData, hasEnoughFlashcardData) => {
  const recommendations = [];
  
  if (!hasQuizData) {
    recommendations.push({
      type: 'quiz_data',
      priority: 'high',
      title: 'Start Taking Quizzes',
      description: 'Take at least 3 quizzes to get meaningful analytics and GPA calculations.',
      action: 'Generate and complete quizzes from your documents'
    });
  } else if (!hasEnoughQuizData) {
    recommendations.push({
      type: 'quiz_data',
      priority: 'medium',
      title: 'Take More Quizzes',
      description: 'Take at least 2 more quizzes to improve the accuracy of your analytics.',
      action: 'Complete more quizzes for better insights'
    });
  }
  
  if (!hasFlashcardData) {
    recommendations.push({
      type: 'flashcard_data',
      priority: 'high',
      title: 'Study Flashcards',
      description: 'Study at least 5 flashcards to get comprehensive learning analytics.',
      action: 'Generate and study flashcards from your documents'
    });
  } else if (!hasEnoughFlashcardData) {
    recommendations.push({
      type: 'flashcard_data',
      priority: 'medium',
      title: 'Study More Flashcards',
      description: 'Study at least 2 more flashcards to improve your mastery tracking.',
      action: 'Continue studying flashcards for better progress tracking'
    });
  }
  
  if (hasEnoughQuizData && hasEnoughFlashcardData) {
    recommendations.push({
      type: 'analytics_ready',
      priority: 'low',
      title: 'Analytics Ready!',
      description: 'You have sufficient data for comprehensive learning analytics.',
      action: 'View your detailed analytics and recommendations'
    });
  }
  
  return recommendations;
};
